import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Header } from '@/components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  BellRing, Trash2, ExternalLink, Search, Wrench, Cog, Palette, Bell, BellOff,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  getSavedListingSearches, deleteSavedListingSearch, updateSavedListingSearch,
  testSavedListingSearch,
} from '@/lib/api';

const FREQ_META = {
  instant: { label: 'Sofort', icon: BellRing, color: 'text-orange-500' },
  daily: { label: 'Täglich', icon: Bell, color: 'text-blue-500' },
  weekly: { label: 'Wöchentlich', icon: Bell, color: 'text-violet-500' },
  off: { label: 'Deaktiviert', icon: BellOff, color: 'text-slate-400' },
};

const VERTICAL_META = {
  spare_parts: { label: 'Ersatzteile', icon: Wrench, color: 'text-orange-500' },
  machines: { label: 'Druckmaschinen', icon: Cog, color: 'text-blue-500' },
};

export default function SavedSearchesPage() {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);

  async function load() {
    setLoading(true);
    try {
      const { data } = await getSavedListingSearches();
      setItems(data || []);
    } catch (e) {
      toast.error('Konnte Suchen nicht laden');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  async function updateFrequency(search_id, frequency) {
    try {
      const { data } = await updateSavedListingSearch(search_id, { frequency });
      setItems((prev) => prev.map((s) => s.search_id === search_id ? data : s));
      toast.success('Benachrichtigung aktualisiert');
    } catch (e) {
      toast.error('Aktualisierung fehlgeschlagen');
    }
  }

  async function removeSearch(search_id) {
    try {
      await deleteSavedListingSearch(search_id);
      setItems((prev) => prev.filter((s) => s.search_id !== search_id));
      toast.success('Suche gelöscht');
    } catch (e) {
      toast.error('Löschen fehlgeschlagen');
    }
  }

  async function testSearch(search_id) {
    try {
      const { data } = await testSavedListingSearch(search_id, 5);
      const count = data?.count ?? 0;
      toast.success(`${count} aktuelle Treffer für diese Suche`);
    } catch (e) {
      toast.error('Test fehlgeschlagen');
    }
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 py-8" data-testid="saved-searches-page">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-6">
          <div className="flex items-center gap-3">
            <BellRing className="w-7 h-7 text-orange-500" />
            <div>
              <h1 className="text-3xl font-bold tracking-tight">Gespeicherte Suchen</h1>
              <p className="text-sm text-muted-foreground">
                Lassen Sie sich per E-Mail über neue passende Inserate informieren.
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button asChild variant="outline" data-testid="go-to-spare-parts-btn">
              <Link to="/listings?vertical=spare_parts"><Wrench className="w-4 h-4 mr-2" />Ersatzteile</Link>
            </Button>
            <Button asChild variant="outline" data-testid="go-to-machines-btn">
              <Link to="/listings?vertical=machines"><Cog className="w-4 h-4 mr-2" />Druckmaschinen</Link>
            </Button>
            <Button asChild variant="outline" data-testid="go-to-paints-btn">
              <Link to="/listings?vertical=paints"><Palette className="w-4 h-4 mr-2" />Farben & Lacke</Link>
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-40" />)}
          </div>
        ) : items.length === 0 ? (
          <Card>
            <CardContent className="py-16 flex flex-col items-center text-center text-muted-foreground">
              <Search className="w-12 h-12 mb-3 opacity-40" />
              <p className="mb-1 font-medium text-foreground">Noch keine gespeicherten Suchen</p>
              <p className="text-sm">
                Öffnen Sie die Inserats-Liste, wenden Sie Filter an und klicken Sie <strong>„Suche speichern"</strong>.
              </p>
              <div className="flex gap-2 mt-4">
                <Button asChild>
                  <Link to="/listings?vertical=spare_parts" data-testid="saved-searches-empty-spare-parts"><Wrench className="w-4 h-4 mr-2" />Ersatzteile durchsuchen</Link>
                </Button>
                <Button asChild variant="outline">
                  <Link to="/listings?vertical=machines" data-testid="saved-searches-empty-machines"><Cog className="w-4 h-4 mr-2" />Maschinen durchsuchen</Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="saved-searches-grid">
            {items.map((s) => <SavedSearchCard
              key={s.search_id} search={s}
              onUpdateFrequency={updateFrequency}
              onRemove={removeSearch}
              onTest={testSearch}
            />)}
          </div>
        )}
      </main>
    </div>
  );
}

function SavedSearchCard({ search, onUpdateFrequency, onRemove, onTest }) {
  const vMeta = VERTICAL_META[search.vertical] || VERTICAL_META.spare_parts;
  const VIcon = vMeta.icon;
  const freqMeta = FREQ_META[search.frequency] || FREQ_META.daily;
  const FIcon = freqMeta.icon;
  const criteriaEntries = Object.entries(search.criteria || {}).filter(([, v]) => v !== '' && v != null);

  return (
    <Card data-testid={`saved-search-card-${search.search_id}`}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1">
            <CardTitle className="text-base flex items-center gap-2">
              <VIcon className={`w-4 h-4 ${vMeta.color}`} />
              {search.name}
            </CardTitle>
            <Badge variant="outline" className="mt-2 gap-1">
              <FIcon className={`w-3 h-3 ${freqMeta.color}`} />
              {freqMeta.label}
            </Badge>
          </div>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="ghost" size="icon" className="text-red-500 hover:text-red-600" data-testid={`saved-search-delete-${search.search_id}`}>
                <Trash2 className="w-4 h-4" />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Suche löschen?</AlertDialogTitle>
                <AlertDialogDescription>
                  „{search.name}" wird dauerhaft entfernt. Sie erhalten keine E-Mail-Alerts mehr.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                <AlertDialogAction onClick={() => onRemove(search.search_id)} data-testid={`saved-search-delete-confirm-${search.search_id}`}>
                  Löschen
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </CardHeader>
      <CardContent className="space-y-3 pt-0">
        {criteriaEntries.length === 0 ? (
          <p className="text-xs text-muted-foreground italic">Keine Filter – alle aktiven Inserate dieses Bereichs.</p>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {criteriaEntries.slice(0, 6).map(([k, v]) => (
              <Badge key={k} variant="secondary" className="text-[11px] font-normal">
                {prettifyKey(k)}: {String(v)}
              </Badge>
            ))}
            {criteriaEntries.length > 6 && (
              <Badge variant="secondary" className="text-[11px] font-normal">+{criteriaEntries.length - 6}</Badge>
            )}
          </div>
        )}
        <div className="flex items-center justify-between gap-2 pt-1">
          <Select
            value={search.frequency}
            onValueChange={(v) => onUpdateFrequency(search.search_id, v)}
          >
            <SelectTrigger className="h-8 text-xs w-[140px]" data-testid={`saved-search-freq-${search.search_id}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="instant">Sofort</SelectItem>
              <SelectItem value="daily">Täglich</SelectItem>
              <SelectItem value="weekly">Wöchentlich</SelectItem>
              <SelectItem value="off">Deaktiviert</SelectItem>
            </SelectContent>
          </Select>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => onTest(search.search_id)} data-testid={`saved-search-test-${search.search_id}`}>
              Testen
            </Button>
            <Button size="sm" asChild data-testid={`saved-search-run-${search.search_id}`}>
              <Link to={buildListingsUrl(search)}>
                <ExternalLink className="w-3.5 h-3.5 mr-1" /> Öffnen
              </Link>
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function prettifyKey(k) {
  const map = {
    search: 'Suche', category_node_id: 'Kategorie', manufacturer_id: 'Hersteller',
    condition: 'Zustand', min_price: 'Min €', max_price: 'Max €',
    country: 'Land', year_min: 'Baujahr ≥', year_max: 'Baujahr ≤',
  };
  return map[k] || k;
}

function buildListingsUrl(search) {
  const params = new URLSearchParams({ vertical: search.vertical });
  Object.entries(search.criteria || {}).forEach(([k, v]) => {
    if (v !== '' && v != null) params.append(k, String(v));
  });
  return `/listings?${params.toString()}`;
}
